import type { ClipboardEvent, DragEvent, KeyboardEvent, ReactNode, RefObject } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { AskCard, ChatAttachment, PageRef } from '@shared/api';
import type { Decision } from '@shared/types';
import type { ChatMessage } from '../../state/useChat';
import type { QueuedChatRequest } from '../../state/chatQueue';
import { taskRows, toolPhrase, type TaskRow } from '../../state/taskRows';
import { TaskList } from './TaskList';
import { ActivityTray, activityTrayContentKey } from './ActivityTray';
import { ThinkingAuraPanel } from './ThinkingAuraPanel';
import { readThinkingAura, saveThinkingAura } from './thinkingAura';
import { agentBarScenarioAt } from './agentBarScenarios';
import {
  createAgentBarPreviewRuntime,
  reduceAgentBarPreview,
  type AgentBarPreviewAction,
  type AgentBarPreviewRuntime,
} from './agentBarPreviewRuntime';
import {
  AGENT_BAR_FILE_ACCEPT,
  addAgentBarFiles,
  agentBarSubmissionText,
  formatAgentBarFileSize,
  pastedAgentBarFiles,
  prepareAgentBarAttachments,
  type AgentBarFile,
} from './agentBarAttachments';
import './dock.css';

/**
 * The one message bar (docs/message-bar-states.md). Replaces the three prior
 * composers (Composer's compact size, FloatingAskBar, build/BuildComposer).
 * Starts from build/BuildComposer's shell so live-build.css's positioning
 * (`.build-composer-dock`, `.composer-stack`, `.composer-panel`) carries
 * over unchanged; `dock.css` restyles the block and input row in the
 * burnt-orange palette and adds the task list.
 */
export interface AgentBarHandle {
  setText: (value: string) => void;
}

/** State that may be owned by a surface when the bar moves between layouts. */
export interface AgentBarState {
  text: string;
  attachments: AgentBarFile[];
  auraPanelOpen: boolean;
  messagePreviewIndex: number | null;
  /** The rep folded the bar into its status pill. Work keeps running underneath. */
  minimized: boolean;
}

export const emptyAgentBarState = (): AgentBarState => ({
  text: '',
  attachments: [],
  auraPanelOpen: false,
  messagePreviewIndex: null,
  minimized: false,
});

export type AgentBarMiniTone = 'idle' | 'running' | 'waiting' | 'done' | 'error';

/**
 * What the minimized pill says: the one row that matters most, so a rep who
 * folded the bar away can still tell it is working, needs them, or finished.
 * Priority: a question beats running work beats a queued send beats a result.
 */
export function agentBarMiniStatus(rows: TaskRow[]): { tone: AgentBarMiniTone; label: string } {
  const waiting = rows.find((row) => row.state === 'waiting');
  if (waiting) return { tone: 'waiting', label: waiting.ask?.question || 'Needs your answer' };
  const running = rows.find((row) => row.state === 'running');
  if (running) return { tone: 'running', label: running.status?.label ?? toolPhrase(running.activeTool) };
  const queued = rows.filter((row) => row.state === 'queued').length;
  if (queued > 0) return { tone: 'running', label: queued === 1 ? '1 request queued' : `${queued} requests queued` };
  const done = [...rows].reverse().find((row) => row.state === 'done');
  if (done) {
    if (done.kind === 'error') return { tone: 'error', label: 'Request failed — open to retry' };
    if (done.kind === 'stopped') return { tone: 'done', label: done.summaryLine ?? 'Stopped.' };
    if (done.kind === 'change') return { tone: 'done', label: done.summaryLine ?? 'Done.' };
    return { tone: 'done', label: 'Reply ready' };
  }
  return { tone: 'idle', label: 'Ask for a change' };
}

/** A workflow-owned question that uses the same waiting/ask presentation as an agent question. */
export interface AgentBarAskState {
  card: AskCard;
  onAnswer: (answer: string) => void;
  lead?: string;
  page?: PageRef;
  dismissible?: boolean;
  /**
   * The card replaces the agent's finished replies while it shows (errors
   * stay). Approve-and-build uses it: the card's own one-line summary is the
   * whole message, not the agent's report above it.
   */
  hidesReplies?: boolean;
}

export function agentBarAskRow(state: AgentBarAskState): TaskRow {
  return {
    key: `agent-bar-ask:${state.card.id}`,
    state: 'waiting',
    request: '',
    text: state.lead ?? '',
    ask: state.card,
    summary: [],
    undo: false,
    versionAtSend: null,
    error: null,
    stopped: false,
    activeTool: null,
    status: null,
    page: state.page ?? null,
    kind: null,
    summaryLine: null,
    dismissible: state.dismissible ?? false,
  };
}

export function agentBarPlaceholder(
  mode: 'start' | 'intake' | 'proposal',
  scopeActive: boolean,
  customPlaceholder?: string,
): string {
  if (customPlaceholder) return customPlaceholder;
  if (scopeActive) return 'Change this page…';
  if (mode === 'start') return 'Brody Pennell, $40k summer campaign';
  return "Ask for a change and I'll take care of it…";
}

export function agentBarTextareaHeight(scrollHeight: number, maxHeight: string): number {
  const parsedMaxHeight = Number.parseFloat(maxHeight);
  return Number.isFinite(parsedMaxHeight) ? Math.min(scrollHeight, parsedMaxHeight) : scrollHeight;
}

function resizeTextareaToContent(textarea: HTMLTextAreaElement): void {
  textarea.style.height = 'auto';
  const scrollHeight = textarea.scrollHeight;
  const height = agentBarTextareaHeight(scrollHeight, getComputedStyle(textarea).maxHeight);
  textarea.style.height = `${height}px`;
  textarea.style.overflowY = scrollHeight > height ? 'auto' : 'hidden';
}

function useAutosizeTextarea(ref: RefObject<HTMLTextAreaElement>, value: string): void {
  useLayoutEffect(() => {
    if (ref.current) resizeTextareaToContent(ref.current);
  }, [ref, value]);

  useLayoutEffect(() => {
    const textarea = ref.current;
    if (!textarea || typeof ResizeObserver === 'undefined') return;

    let measuredWidth = textarea.getBoundingClientRect().width;
    const observer = new ResizeObserver((entries) => {
      const nextWidth = entries[0]?.contentRect.width ?? textarea.getBoundingClientRect().width;
      if (Math.abs(nextWidth - measuredWidth) < 0.5) return;
      measuredWidth = nextWidth;
      resizeTextareaToContent(textarea);
    });
    observer.observe(textarea);
    return () => observer.disconnect();
  }, [ref]);
}

/**
 * The canonical proposal creation bar. Start and intake use its compact input
 * API; proposal surfaces additionally supply task data and action callbacks.
 */
export const AgentBar = forwardRef<
  AgentBarHandle,
  {
    mode?: 'start' | 'intake' | 'proposal';
    messages?: ChatMessage[];
    queue?: QueuedChatRequest[];
    /** Retained while proposal surfaces own the decision ledger outside this bar. */
    decisions?: Decision[];
    onSend: (text: string, attachments?: ChatAttachment[]) => void | Promise<void>;
    /**
     * Retained for proposal-owned decision handling outside the AgentBar.
     */
    onResolveDecision?: (decision: Decision, answerIndex: number) => void;
    /**
     * Retained for proposal-owned decision handling outside the AgentBar.
     */
    onDismissDecision?: (decision: Decision) => void;
    onStop?: () => void;
    onRemoveQueued?: (userId: string) => void;
    onDismiss?: () => void;
    /** The done row's Undo — carries the row so the caller can restore its versionAtSend (docs/composer-cards.md 5c). */
    onUndo?: (row: TaskRow) => void;
    /** A page is open — the placeholder narrows to "Change this page…" (2e). No separate label. */
    scopeActive?: boolean;
    /** An optional non-question workflow surface, such as missing details. */
    panel?: ReactNode;
    /** A workflow-owned question. Authorization and choices belong in ask state, not a passive panel. */
    ask?: AgentBarAskState;
    disabled?: boolean;
    placeholder?: string;
    autoFocus?: boolean;
    clearOnSend?: boolean;
    trailing?: ReactNode;
    chip?: ReactNode;
    className?: string;
    state?: AgentBarState;
    onStateChange?: (state: AgentBarState) => void;
  }
>(function AgentBar(
  { mode = 'proposal', messages = [], queue = [], decisions = [], onSend, onResolveDecision, onDismissDecision, onStop = () => undefined, onRemoveQueued = () => undefined, onDismiss = () => undefined, onUndo = () => undefined, scopeActive = false, panel, ask, disabled = false, placeholder: customPlaceholder, autoFocus = false, clearOnSend = true, trailing, chip, className = '', state: controlledState, onStateChange },
  forwardedRef,
) {
  // Decision state remains owned by proposal surfaces. The AgentBar no
  // longer presents it, but keeps this input contract during that migration.
  void decisions;
  void onResolveDecision;
  void onDismissDecision;
  const [uncontrolledState, setUncontrolledState] = useState(emptyAgentBarState);
  const barState = controlledState ?? uncontrolledState;
  const setBarState = (update: Partial<AgentBarState>) => {
    const next = { ...barState, ...update };
    if (controlledState) onStateChange?.(next);
    else setUncontrolledState(next);
  };
  const previewScenario = agentBarScenarioAt(barState.messagePreviewIndex);
  const previewing = previewScenario !== null;
  const [previewUiState, setPreviewUiState] = useState<{
    scenarioId: string | null;
    text: string;
  }>({
    scenarioId: null,
    text: '',
  });
  const scenarioInitialState = previewScenario?.initialState;
  const activePreviewState = previewScenario && previewUiState.scenarioId === previewScenario.id
    ? previewUiState
    : {
        scenarioId: previewScenario?.id ?? null,
        text: scenarioInitialState?.text ?? '',
      };
  const interactiveState = previewing ? activePreviewState : barState;
  const [previewRuntime, setPreviewRuntime] = useState<AgentBarPreviewRuntime | null>(null);
  const [previewResetRevision, setPreviewResetRevision] = useState(0);
  const activePreviewRuntime = previewScenario
    ? previewRuntime?.scenarioId === previewScenario.id
      ? previewRuntime
      : createAgentBarPreviewRuntime(previewScenario)
    : null;
  const dispatchPreview = (action: AgentBarPreviewAction) => {
    if (!previewScenario) return;
    setPreviewRuntime((current) => reduceAgentBarPreview(
      current?.scenarioId === previewScenario.id ? current : createAgentBarPreviewRuntime(previewScenario),
      action,
    ));
  };
  const resetPreview = () => {
    if (!previewScenario) return;
    setPreviewRuntime(createAgentBarPreviewRuntime(previewScenario));
    setPreviewResetRevision((current) => current + 1);
    setPreviewUiState({
      scenarioId: previewScenario.id,
      text: scenarioInitialState?.text ?? '',
    });
  };
  const setInteractiveState = (update: Partial<AgentBarState>) => {
    if (!previewScenario) {
      setBarState(update);
      return;
    }
    setPreviewUiState((current) => {
      const base = current.scenarioId === previewScenario.id ? current : activePreviewState;
      return {
        ...base,
        text: update.text ?? base.text,
      };
    });
  };
  const text = interactiveState.text;
  const attachments = barState.attachments;
  const [auraSettings, setAuraSettings] = useState(readThinkingAura);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [dropActive, setDropActive] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [preparingAttachments, setPreparingAttachments] = useState(false);
  useAutosizeTextarea(inputRef, text);
  // Only the proposal surface floats over the canvas; intake/start stay full.
  const minimized = mode === 'proposal' && barState.minimized === true;
  const minimizedRef = useRef(minimized);
  minimizedRef.current = minimized;
  const setBarStateRef = useRef(setBarState);
  setBarStateRef.current = setBarState;
  const expandRef = useRef(false);
  useEffect(() => {
    if (!minimized && expandRef.current) {
      expandRef.current = false;
      inputRef.current?.focus();
    }
  }, [minimized]);

  useEffect(() => { saveThinkingAura(auraSettings); }, [auraSettings]);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReducedMotion(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useImperativeHandle(forwardedRef, () => ({
    setText: (value: string) => {
      setBarState({ text: value });
      inputRef.current?.focus();
    },
  }));

  useEffect(() => {
    // ⌘J, not ⌘K: the sidebar's proposal search owns ⌘K.
    const focus = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        if (minimizedRef.current) setBarStateRef.current({ minimized: false });
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', focus);
    return () => document.removeEventListener('keydown', focus);
  }, []);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const rows = useMemo(() => taskRows(messages, queue), [messages, queue]);
  // An in-flight agent question wins. The workflow question resumes as soon
  // as that turn settles, keeping the bar to one active question at a time.
  const hasActiveAgentRow = rows.some((row) => row.state === 'running' || row.state === 'waiting' || row.state === 'queued');
  const workflowAskRow = useMemo(() => (ask && !hasActiveAgentRow ? agentBarAskRow(ask) : null), [ask, hasActiveAgentRow]);
  const liveRows = workflowAskRow
    ? [...(ask?.hidesReplies ? rows.filter((row) => row.state !== 'done' || row.kind === 'error') : rows), workflowAskRow]
    : rows;
  // Preview scenarios use the same task rows and component tree as live
  // data. Their primary key stays stable across scenarios so the layout
  // transition animates a real running -> waiting -> done task lifecycle.
  const displayedRows = activePreviewRuntime?.rows ?? liveRows;
  const displayedPanel = previewing ? null : panel;
  const showsTasks = mode !== 'start';
  const hasBlock = Boolean(displayedPanel) || (showsTasks && displayedRows.length > 0);
  // The tray transitions only when its composition changes. State changes for
  // an existing task keep this key stable so the persistent TaskRowItem owns a
  // single content/height transition instead of being duplicated by the tray.
  const trayContentKey = activityTrayContentKey(Boolean(displayedPanel), displayedRows.map((row) => row.key));
  const placeholder = agentBarPlaceholder(mode, scopeActive, customPlaceholder);
  /* Dims the send button while a task runs (msgbar--busy) — presentation
     only. The field stays live: a send while busy adds a queued row rather
     than being blocked (docs/message-bar-states.md). A waiting row whose
     turn is still working behind a keepWorking question (row.working) is
     deliberately NOT busy: the rep is being asked to answer, so the send
     button stays bright — the typed answer queues behind the turn just as
     any send while busy would (docs/composer-cards.md 10j). */
  const busy = displayedRows.some((row) => row.state === 'running');

  const addFiles = (incoming: Iterable<File>) => {
    if (disabled || preparingAttachments) return;
    const next = addAgentBarFiles(attachments, incoming);
    setAttachmentError(next.error);
    if (next.files !== attachments) setBarState({ attachments: next.files });
  };

  const removeFile = (id: string) => {
    setBarState({ attachments: attachments.filter((attachment) => attachment.id !== id) });
    setAttachmentError(null);
  };

  const dragCarriesFiles = (event: DragEvent<HTMLElement>) => Array.from(event.dataTransfer.types).includes('Files');
  const onDragEnter = (event: DragEvent<HTMLElement>) => {
    if (!dragCarriesFiles(event)) return;
    event.preventDefault();
    if (disabled || preparingAttachments) return;
    dragDepth.current += 1;
    setDropActive(true);
  };
  const onDragOver = (event: DragEvent<HTMLElement>) => {
    if (!dragCarriesFiles(event)) return;
    event.preventDefault();
    if (disabled || preparingAttachments) return;
    event.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = (event: DragEvent<HTMLElement>) => {
    if (!dragCarriesFiles(event)) return;
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDropActive(false);
  };
  const onDrop = (event: DragEvent<HTMLElement>) => {
    if (!dragCarriesFiles(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDropActive(false);
    if (disabled || preparingAttachments) return;
    addFiles(event.dataTransfer.files);
  };

  /* A pasted screenshot or copied image file attaches exactly like a drop.
     Plain-text pastes fall through to the textarea untouched. */
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = pastedAgentBarFiles(event.clipboardData);
    if (files.length === 0) return;
    event.preventDefault();
    addFiles(files);
  };

  const submit = async () => {
    if ((!text.trim() && attachments.length === 0) || disabled || preparingAttachments) return;
    const value = agentBarSubmissionText(text, attachments);
    if (previewing) {
      if (clearOnSend) setInteractiveState({ text: '' });
      if (clearOnSend) setBarState({ attachments: [] });
      dispatchPreview({ type: 'send', text: value });
      return;
    }
    setPreparingAttachments(true);
    setAttachmentError(null);
    try {
      const prepared = await prepareAgentBarAttachments(attachments);
      await onSend(value, prepared);
      if (clearOnSend) setBarState({ text: '', attachments: [] });
    } catch (error) {
      setAttachmentError(error instanceof Error ? error.message : 'The files could not be attached.');
    } finally {
      setPreparingAttachments(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  const attachmentControls = (
    <>
      {attachments.length > 0 || attachmentError ? (
        <div className="agent-bar-attachment-region">
          {attachments.length > 0 ? (
            <div className="agent-bar-attachments" aria-label="Attached files">
              {attachments.map((attachment) => (
                <span className={`agent-bar-attachment${attachment.mediaType.startsWith('image/') ? ' agent-bar-attachment--image' : ''}`} key={attachment.id}>
                  <AttachmentPreview attachment={attachment} />
                  <span className="agent-bar-attachment-copy">
                    <span className="agent-bar-attachment-name">{attachment.file.name}</span>
                    <span className="agent-bar-attachment-size">{formatAgentBarFileSize(attachment.file.size)}</span>
                  </span>
                  <button type="button" onClick={() => removeFile(attachment.id)} aria-label={`Remove ${attachment.file.name}`}>×</button>
                </span>
              ))}
            </div>
          ) : null}
          {attachmentError ? <p className="agent-bar-attachment-error" role="alert">{attachmentError}</p> : null}
        </div>
      ) : null}
      <input
        ref={fileInputRef}
        className="agent-bar-file-input"
        type="file"
        accept={AGENT_BAR_FILE_ACCEPT}
        multiple
        tabIndex={-1}
        onChange={(event) => {
          if (event.target.files) addFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </>
  );

  const attachButton = (
    <button
      type="button"
      className="agent-bar-attach"
      onClick={() => fileInputRef.current?.click()}
      disabled={disabled || preparingAttachments}
      aria-label="Attach files"
      title="Attach PDF, image, or text files"
    >
      <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <path d="M7.2 10.7 12.6 5.3a3 3 0 0 1 4.2 4.2l-7.1 7.1a5 5 0 0 1-7.1-7.1l7-7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );

  const dropOverlay = dropActive ? (
    <div className="agent-bar-drop-overlay" aria-hidden="true">
      <span>Drop files to attach</span>
      <small>PDF, images, text, CSV, JSON</small>
    </div>
  ) : null;

  if (minimized) {
    const mini = agentBarMiniStatus(displayedRows);
    return (
      <div className={`agent-bar agent-bar--${mode} agent-bar--minimized build-composer-dock composer-dock ${className}`}>
        <button
          type="button"
          className={`agent-bar-mini agent-bar-mini--${mini.tone}`}
          onClick={() => {
            expandRef.current = true;
            setBarState({ minimized: false });
          }}
          aria-label={`Open the agent bar. ${mini.label}`}
          title="Open (⌘J)"
        >
          {mini.tone === 'running' ? (
            <span className="dock-row-dots" aria-hidden="true"><span /><span /><span /></span>
          ) : (
            <span className="agent-bar-mini-mark" aria-hidden="true">
              {mini.tone === 'done' ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.5 6.3 5 8.6l4.6-5.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              ) : mini.tone === 'error' ? '!' : mini.tone === 'waiting' ? '?' : (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 13V3.4M3.8 7.6 8 3.2l4.2 4.4" /></svg>
              )}
            </span>
          )}
          <span className="agent-bar-mini-label" aria-live="polite">{mini.label}</span>
          <svg className="agent-bar-mini-chevron" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M4 10l4-4 4 4" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
    );
  }

  if (mode === 'start') {
    return (
      <div className={`agent-bar agent-bar--start composer composer-large composer-start${dropActive ? ' agent-bar--drop-active' : ''} ${className}`} onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
        {dropOverlay}
        {chip}
        <div className="composer-field">
          {attachmentControls}
          <textarea ref={inputRef} className="composer-input" value={text} placeholder={placeholder} onChange={(event) => setBarState({ text: event.target.value })} onKeyDown={onKeyDown} onPaste={onPaste} disabled={disabled || preparingAttachments} rows={1} spellCheck />
          {trailing}
          {attachButton}
          <button type="button" className="composer-send" onClick={() => void submit()} disabled={disabled || preparingAttachments || (!text.trim() && attachments.length === 0)} aria-label="Send message">
            <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true"><path d="M8 13.5V3M8 3 3.5 7.5M8 3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`agent-bar agent-bar--${mode} build-composer-dock composer-dock${dropActive ? ' agent-bar--drop-active' : ''} ${className}`} onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      {dropOverlay}
      {barState.auraPanelOpen ? <ThinkingAuraPanel settings={auraSettings} onChange={setAuraSettings} onClose={() => setBarState({ auraPanelOpen: false })} reducedMotion={reducedMotion} previewIndex={barState.messagePreviewIndex} onPreviewIndexChange={(messagePreviewIndex) => setBarState({ messagePreviewIndex })} previewAction={activePreviewRuntime?.lastAction ?? null} onResetPreview={resetPreview} /> : null}
      <div className="composer-stack">
        <ActivityTray contentKey={trayContentKey} hasContent={hasBlock} settings={auraSettings} reducedMotion={reducedMotion}>
          {hasBlock ? (
            <>
            {displayedPanel ? <div className="composer-activity-item composer-panel"><div className="composer-panel-content">{displayedPanel}</div></div> : null}
            {showsTasks && displayedRows.length > 0 ? (
              <div className="composer-activity-item composer-dock-tasks">
                <TaskList
                  key={previewing ? `preview-reset-${previewResetRevision}` : 'live'}
                  rows={displayedRows}
                  onSend={previewing ? (request) => dispatchPreview({ type: 'retry', request }) : onSend}
                  onAskAnswer={previewing ? (_row, answer) => dispatchPreview({ type: 'answer', text: answer }) : (row, answer) => {
                    if (workflowAskRow && row.key === workflowAskRow.key) ask?.onAnswer(answer);
                    else onSend(answer);
                  }}
                  onStop={previewing ? () => dispatchPreview({ type: 'stop' }) : onStop}
                  onRemoveQueued={previewing ? (rowKey) => dispatchPreview({ type: 'remove-queued', rowKey }) : onRemoveQueued}
                  onDismiss={previewing ? () => dispatchPreview({ type: 'dismiss' }) : onDismiss}
                  onUndo={previewing ? (row) => dispatchPreview({ type: 'undo', rowKey: row.key }) : onUndo}
                  auraSettings={auraSettings}
                  reducedMotion={reducedMotion}
                />
              </div>
            ) : null}
            </>
          ) : null}
        </ActivityTray>
        <form
          className={`composer-dock-input${busy ? ' composer-dock-input-busy' : ''}`}
          aria-label={placeholder}
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          aria-busy={preparingAttachments}
        >
          {attachmentControls}
          <textarea
            ref={inputRef}
            value={text}
            rows={1}
            onChange={(event) => setInteractiveState({ text: event.target.value })}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            placeholder={placeholder}
            aria-label={placeholder}
            disabled={disabled || preparingAttachments}
          />
          {trailing}
          {mode === 'proposal' ? (
            <button
              type="button"
              className="agent-bar-minimize"
              onClick={() => setBarState({ minimized: true })}
              aria-label="Minimize the agent bar"
              title="Minimize — work keeps running"
            >
              <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          ) : null}
          {attachButton}
          <button type="submit" className="composer-dock-send" disabled={disabled || preparingAttachments || (!text.trim() && attachments.length === 0)} aria-label="Send request">
            <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 13V3.4M3.8 7.6 8 3.2l4.2 4.4" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
});

/** An image attachment shows as a thumbnail; anything else keeps the file glyph. */
function AttachmentPreview({ attachment }: { attachment: AgentBarFile }) {
  const isImage = attachment.mediaType.startsWith('image/');
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!isImage) return undefined;
    const objectUrl = URL.createObjectURL(attachment.file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [attachment.file, isImage]);

  if (isImage) {
    return (
      <span className="agent-bar-attachment-thumb" aria-hidden="true">
        {url ? <img src={url} alt="" /> : null}
      </span>
    );
  }
  return (
    <span className="agent-bar-attachment-icon" aria-hidden="true">
      <svg width="13" height="15" viewBox="0 0 13 15" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M2.2.8h5.2l3.4 3.4v10H2.2z" strokeLinejoin="round" />
        <path d="M7.4.8v3.4h3.4" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
